import React, { useEffect, useState } from 'react';
import { Activity, Users, Clock, DollarSign, ArrowRight, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// --- Helpers de UI ---
const countryFlags = { CO: '🇨🇴', VE: '🇻🇪', US: '🇺🇸', PE: '🇵🇪', PA: '🇵🇦', EC: '🇪🇨' };
const getFlag = (code) => countryFlags[code?.toUpperCase()] || '🌍';

const STATUS_COLORS = {
  Procesando: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  Completado: 'bg-green-50 text-green-700 border-green-200',
  Cancelada: 'bg-red-50 text-red-700 border-red-200',
  Creando: 'bg-slate-50 text-slate-700 border-slate-200',
  'En pago': 'bg-blue-50 text-blue-700 border-blue-200',
};

// --- Custom Tooltip para el Gráfico ---
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-100 p-3 rounded-xl shadow-lg">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{label}</p>
        <p className="text-sm font-bold text-indigo-600 flex items-center gap-2">
          {payload[0].value} <span className="text-slate-600 font-medium text-xs">Completadas</span>
        </p>
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const [stats, setStats] = useState({ users: 0, transactions: 0, pending: 0, completed: 0 });
  const [recent, setRecent] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      // Métricas rápidas
      const usersRes = await supabase.from('users').select('id', { count: 'exact', head: true });
      const txRes = await supabase.from('transactions').select('id', { count: 'exact', head: true });
      const pendRes = await supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('status', 'Procesando');
      const completedRes = await supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('status', 'Completado');

      // Transacciones recientes
      const recentTx = await supabase
        .from('transactions')
        .select('id, user_id, origin_country, destination_country, amount, received_amount, status, created_at')
        .order('created_at', { ascending: false })
        .limit(6);

      // Datos para gráfico de transacciones completadas últimos 7 días
      const chartTx = await supabase
        .from('transactions')
        .select('created_at')
        .eq('status', 'Completado')
        .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());

      // Agrupar por día
      const counts = {};
      chartTx.data.forEach(tx => {
        const day = new Date(tx.created_at).toLocaleDateString();
        counts[day] = (counts[day] || 0) + 1;
      });
      const chartArray = Object.entries(counts).map(([day, count]) => ({ day, count }));

      setStats({
        users: usersRes.count || 0,
        transactions: txRes.count || 0,
        pending: pendRes.count || 0,
        completed: completedRes.count || 0
      });
      setRecent(recentTx.data || []);
      setChartData(chartArray);
    } catch (err) {
      console.error('Error cargando métricas del dashboard', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(); // carga inicial

    // Actualización en tiempo real cada 10 segundos
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      
      {/* HEADER */}
      <header className="mb-8">
        <h2 className="text-2xl font-black text-slate-800 tracking-tight">Dashboard General</h2>
        <p className="text-sm text-slate-500 font-medium mt-1">Resumen rápido de la actividad de tu plataforma.</p>
      </header>

      {/* CARDS DE MÉTRICAS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        
        {/* Usuarios */}
        <div className="bg-white p-6 rounded-[24px] shadow-sm border border-slate-100 flex items-center gap-5 relative overflow-hidden group hover:shadow-md transition-all">
          <div className="absolute -right-4 -top-4 w-16 h-16 bg-blue-50 rounded-full group-hover:scale-150 transition-transform duration-500 ease-out z-0"></div>
          <div className="p-3.5 bg-blue-50 text-blue-600 rounded-2xl relative z-10">
            <Users className="w-6 h-6" />
          </div>
          <div className="relative z-10">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Usuarios</p>
            {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-300 mt-1" /> : <p className="text-2xl font-black text-slate-800 leading-none">{stats.users.toLocaleString()}</p>}
          </div>
        </div>

        {/* Pendientes */}
        <div className="bg-white p-6 rounded-[24px] shadow-sm border border-slate-100 flex items-center gap-5 relative overflow-hidden group hover:shadow-md transition-all">
          <div className="absolute -right-4 -top-4 w-16 h-16 bg-yellow-50 rounded-full group-hover:scale-150 transition-transform duration-500 ease-out z-0"></div>
          <div className="p-3.5 bg-yellow-50 text-yellow-600 rounded-2xl relative z-10">
            <Clock className="w-6 h-6" />
          </div>
          <div className="relative z-10">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Pendientes</p>
            {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-300 mt-1" /> : <p className="text-2xl font-black text-slate-800 leading-none">{stats.pending.toLocaleString()}</p>}
          </div>
        </div>

        {/* Transacciones Totales */}
        <div className="bg-white p-6 rounded-[24px] shadow-sm border border-slate-100 flex items-center gap-5 relative overflow-hidden group hover:shadow-md transition-all">
          <div className="absolute -right-4 -top-4 w-16 h-16 bg-emerald-50 rounded-full group-hover:scale-150 transition-transform duration-500 ease-out z-0"></div>
          <div className="p-3.5 bg-emerald-50 text-emerald-600 rounded-2xl relative z-10">
            <DollarSign className="w-6 h-6" />
          </div>
          <div className="relative z-10">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Transacciones</p>
            {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-300 mt-1" /> : <p className="text-2xl font-black text-slate-800 leading-none">{stats.transactions.toLocaleString()}</p>}
          </div>
        </div>

        {/* Completadas */}
        <div className="bg-white p-6 rounded-[24px] shadow-sm border border-slate-100 flex items-center gap-5 relative overflow-hidden group hover:shadow-md transition-all">
          <div className="absolute -right-4 -top-4 w-16 h-16 bg-indigo-50 rounded-full group-hover:scale-150 transition-transform duration-500 ease-out z-0"></div>
          <div className="p-3.5 bg-indigo-50 text-indigo-600 rounded-2xl relative z-10">
            <Activity className="w-6 h-6" />
          </div>
          <div className="relative z-10">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Completadas</p>
            {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-300 mt-1" /> : <p className="text-2xl font-black text-slate-800 leading-none">{stats.completed.toLocaleString()}</p>}
          </div>
        </div>
      </div>

      {/* GRÁFICO Y TABLA (Layout Grid) */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        
        {/* GRÁFICO (Ocupa 2/3 en pantallas muy grandes) */}
        <div className="bg-white p-6 md:p-8 rounded-[24px] shadow-sm border border-slate-100 xl:col-span-2 flex flex-col">
          <h3 className="text-sm font-black uppercase tracking-widest text-slate-800 mb-6">Completadas (Últimos 7 días)</h3>
          
          <div className="flex-1 min-h-[300px] w-full">
            {loading && chartData.length === 0 ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-200 mb-2" />
                <span className="text-xs font-bold uppercase tracking-widest">Cargando métricas...</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="day" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 600 }} 
                    dy={10} 
                  />
                  <YAxis 
                    allowDecimals={false} 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 600 }} 
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#e2e8f0', strokeWidth: 1, strokeDasharray: '3 3' }} />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#4f46e5" 
                    strokeWidth={3} 
                    dot={{ r: 4, fill: '#4f46e5', strokeWidth: 2, stroke: '#ffffff' }} 
                    activeDot={{ r: 6, fill: '#4f46e5', stroke: '#e0e7ff', strokeWidth: 4 }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* TRANSACCIONES RECIENTES (Ocupa 1/3 en pantallas muy grandes) */}
        <div className="bg-white p-6 md:p-8 rounded-[24px] shadow-sm border border-slate-100 xl:col-span-1 flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-black uppercase tracking-widest text-slate-800">Recientes</h3>
          </div>

          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
            {loading && recent.length === 0 ? (
               <div className="w-full h-40 flex items-center justify-center text-slate-400">
                 <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
               </div>
            ) : recent.length === 0 ? (
               <div className="text-center py-10 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                 <Activity className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                 <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Sin actividad reciente</p>
               </div>
            ) : (
              <div className="space-y-4">
                {recent.map(tx => (
                  <div key={tx.id} className="flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-colors">
                    
                    {/* Izquierda: Ruta y Fecha */}
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col items-center justify-center bg-slate-100 text-slate-600 rounded-lg p-1.5 shrink-0">
                        <span className="text-base leading-none mb-0.5">{getFlag(tx.origin_country)}</span>
                        <span className="text-base leading-none">{getFlag(tx.destination_country)}</span>
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 text-[13px] leading-tight">
                          {Number(tx.amount).toLocaleString('es-CO')}
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                          {new Date(tx.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>

                    {/* Derecha: Estado */}
                    <div className="text-right flex flex-col items-end gap-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[9px] font-black uppercase tracking-widest ${STATUS_COLORS[tx.status] || 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                        {tx.status === 'Completado' ? <CheckCircle className="w-2.5 h-2.5" /> : tx.status === 'Procesando' ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <XCircle className="w-2.5 h-2.5" />}
                        {tx.status}
                      </span>
                      <span className="text-[9px] font-mono font-medium text-slate-400">#{tx.id}</span>
                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

